import axios from "axios";
import i18n from "../i18n/config";
import { mockApi } from "./mockApi.js";
import {
  API_ENDPOINTS,
  buildUrl,
  validateEndpointParams,
} from "./endpoints.js";
import { describeApiError } from "../utils/apiErrors";
import { EVENTO_LIMITE, LIMITE_DEL_CODICE, eLimiteDelPiano } from "../utils/limitiPiano";

// Authentication state management
class AuthState {
  constructor() {
    this.isAuthenticated = false;
    this.user = null;
    this.tenants = [];
    this.listeners = new Set();
    this.refreshPromise = null;
  }

  setAuthenticated(isAuth, user = null, tenants = []) {
    this.isAuthenticated = isAuth;
    this.user = user;
    this.tenants = tenants || [];
    this.notifyListeners();
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  notifyListeners() {
    this.listeners.forEach((listener) =>
      listener({
        isAuthenticated: this.isAuthenticated,
        user: this.user,
        tenants: this.tenants,
      })
    );
  }

  async refreshSession() {
    // Prevent multiple concurrent refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this._performRefresh();
    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.refreshPromise = null;
    }
  }

  async _performRefresh() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.AUTH.ME);
      this.setAuthenticated(
        true,
        response.data.user,
        response.data.tenants || []
      );
      return true;
    } catch (error) {
      console.error("Session refresh failed:", error);
      // Solo un 401 dice davvero "non sei autenticato". Un errore di rete o
      // un 5xx (es. API in riavvio in dev) non deve buttare via la sessione:
      // lo stato resta quello che era e si riproverà alla prossima chiamata.
      if (error?.status === 401 || error?.response?.status === 401) {
        this.setAuthenticated(false, null, []);
        return false;
      }
      return this.isAuthenticated;
    }
  }
}

// Global auth state instance
export const authState = new AuthState();

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || API_ENDPOINTS.BASE_URL,
  timeout: 30000,
  withCredentials: true, // Include cookies for session management
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor for adding auth headers and logging
apiClient.interceptors.request.use(
  (config) => {
    // Add request timestamp for debugging
    config.metadata = { startTime: new Date() };

    // Add request ID for correlation
    config.headers["X-Request-ID"] = `req_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    // Add active tenant ID header if available
    const activeTenantId = localStorage.getItem("activeTenantId");
    if (activeTenantId && activeTenantId !== "undefined" && activeTenantId !== "null") {
      config.headers["X-Tenant-Id"] = activeTenantId;
    }

    // Log request in development
    if (import.meta.env.DEV) {
      console.log(
        `🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`,
        {
          headers: config.headers,
          data: config.data,
        }
      );
    }

    return config;
  },
  (error) => {
    console.error("❌ Request interceptor error:", error);
    return Promise.reject(error);
  }
);

/**
 * Costruisce un Error "ricco" a partire dalla risposta dell'API.
 *
 * Prima l'interceptor rigettava con `new Error(data.error || data.message)`:
 * vinceva il CODICE macchina (es. "bots_limit_reached") e si perdevano sia il
 * messaggio umano sia i campi strutturati (limit, current, details...), che
 * l'API manda già pronti. Qui il messaggio resta leggibile per l'utente e
 * tutto il resto viaggia sull'errore, così chi chiama può mappare i casi noti.
 *
 * @param {string} message Testo mostrabile all'utente.
 * @param {object} error   Errore axios originale.
 */
const buildApiError = (message, error) => {
  const data = error?.response?.data;
  const apiError = new Error(message);
  apiError.code = data?.error; // es. "bots_limit_reached", "quota_exceeded"
  apiError.status = error?.response?.status;
  apiError.details = data?.details; // errori di validazione per campo
  apiError.data = data; // limit / current / requested / message grezzo
  apiError.response = error?.response; // compat: chi legge la risposta grezza
  return apiError;
};

// Response interceptor for error handling, logging, and auth management
apiClient.interceptors.response.use(
  (response) => {
    // Calculate request duration
    const duration = new Date() - response.config.metadata.startTime;

    // Log response in development
    if (import.meta.env.DEV) {
      console.log(
        `✅ API Response: ${response.status} ${response.config.url} (${duration}ms)`,
        response.data
      );
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Calculate request duration if available
    const duration = originalRequest?.metadata?.startTime
      ? new Date() - originalRequest.metadata.startTime
      : "unknown";

    // Log error with more context
    console.error(
      `❌ API Error: ${error.response?.status || "Network"} ${
        originalRequest?.url
      } (${duration}ms)`,
      {
        error: error.message,
        response: error.response?.data,
        config: originalRequest,
      }
    );

    // Handle 401 Unauthorized with automatic retry
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry for auth endpoints (login, signup, me) - these should fail immediately
      // /auth/me is especially important because it's used BY the refresh logic itself!
      const isAuthEndpoint = originalRequest?.url?.includes('/auth/login') || 
                            originalRequest?.url?.includes('/auth/signup') ||
                            originalRequest?.url?.includes('/auth/me');
      
      if (isAuthEndpoint) {
        // For auth endpoints, clear state and return error with server message.
        // buildApiError conserva status/response sull'errore: chi sta sopra
        // deve poter distinguere un 401 vero da un errore transitorio.
        authState.setAuthenticated(false, null, []);
        const serverMessage = error.response?.data?.error || error.response?.data?.message;
        return Promise.reject(
          buildApiError(serverMessage || "Authentication failed", error)
        );
      }

      // For other endpoints, try to refresh the session
      originalRequest._retry = true;

      try {
        // Attempt to refresh the session
        const refreshed = await authState.refreshSession();

        if (refreshed) {
          // Retry the original request
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        console.error("Session refresh failed:", refreshError);
      }

      // If refresh failed, clear auth state
      authState.setAuthenticated(false, null, []);

      // Redirect to login page with current URL as return parameter
      //const currentUrl = window.location.pathname + window.location.search;
      //const encodedReturnUrl = encodeURIComponent(currentUrl);
      //window.location.href = `/login?returnUrl=${encodedReturnUrl}`;

      return Promise.reject(new Error("Authentication required"));
    }

    // Handle other error cases
    if (error.response?.status === 403) {
      return Promise.reject(
        buildApiError(i18n.t("errors.api.forbidden"), error)
      );
    }

    if (error.response?.status === 429) {
      return Promise.reject(
        buildApiError(i18n.t("errors.api.tooManyRequests"), error)
      );
    }

    if (error.response?.status >= 500) {
      // Il messaggio grezzo di un 5xx può contenere dettagli interni: non lo
      // usiamo come testo utente, ma resta leggibile in err.data.message per
      // chi vuole mapparlo deliberatamente (es. l'analisi del sito).
      return Promise.reject(
        buildApiError(i18n.t("errors.api.server"), error)
      );
    }

    if (!error.response) {
      // Network error: marcato con un codice così è riconoscibile senza
      // confrontare il testo del messaggio.
      const networkError = new Error(i18n.t("errors.api.network"));
      networkError.code = "network_error";
      return Promise.reject(networkError);
    }

    // Client errors (4xx): preferisci il messaggio umano dell'API, tenendo il
    // codice macchina e i campi strutturati su err.code / err.data.
    const data = error.response?.data;
    const apiError = buildApiError(data?.message || data?.error || "Request failed", error);
    // Limite del piano: il messaggio dell'API e' in inglese e per macchine;
    // si mette quello tradotto e si avvisa la pagina (AvvisoLimitePiano).
    if (eLimiteDelPiano(apiError)) {
      apiError.message = describeApiError(apiError, i18n.t.bind(i18n)) || apiError.message;
      window.dispatchEvent(
        new CustomEvent(EVENTO_LIMITE, {
          detail: { code: apiError.code, message: apiError.message, bisogno: { limite: LIMITE_DEL_CODICE[apiError.code] } },
        })
      );
    }
    return Promise.reject(apiError);
  }
);

// Check if we should use mock API
const useMockApi = import.meta.env.VITE_USE_MOCK_API === "true";
// Helper function to handle API responses consistently
const handleResponse = (response) => {
  return response.data;
};

// Helper function to validate required parameters (legacy - use validateEndpointParams instead)
const validateParams = (params, requiredFields) => {
  for (const field of requiredFields) {
    if (params[field] === undefined || params[field] === null) {
      throw new Error(`Missing required parameter: ${field}`);
    }
  }
};

// API helper functions with enhanced error handling and validation
export const api = {
  // Generic HTTP methods with consistent response handling
  get: async (url, config = {}) => {
    if (useMockApi) {
      const response = await mockApi.get(url, config);
      return handleResponse(response);
    } else {
      const response = await apiClient.get(url, config);
      return handleResponse(response);
    }
  },

  post: async (url, data = {}, config = {}) => {
    if (useMockApi) {
      const response = await mockApi.post(url, data, config);
      return handleResponse(response);
    } else {
      const response = await apiClient.post(url, data, config);
      return handleResponse(response);
    }
  },

  put: async (url, data = {}, config = {}) => {
    if (useMockApi) {
      const response = await mockApi.put(url, data, config);
      return handleResponse(response);
    } else {
      const response = await apiClient.put(url, data, config);
      return handleResponse(response);
    }
  },

  patch: async (url, data = {}, config = {}) => {
    if (useMockApi) {
      const response = await mockApi.patch(url, data, config);
      return handleResponse(response);
    } else {
      const response = await apiClient.patch(url, data, config);
      return handleResponse(response);
    }
  },

  delete: async (url, config = {}) => {
    if (useMockApi) {
      const response = await mockApi.delete(url, config);
      return handleResponse(response);
    } else {
      const response = await apiClient.delete(url, config);
      return handleResponse(response);
    }
  },

  // Health check
  health: async () => {
    return useMockApi
      ? await mockApi.health()
      : await api.get(API_ENDPOINTS.HEALTH.BASIC);
  },

  // Authentication services
  auth: {
    // Native authentication endpoints
    signup: async (userData) => {
      validateEndpointParams("signup", userData, ["email", "name", "password"]);
      try {
        const result = useMockApi
          ? await mockApi.auth.signup(userData)
          : await api.post(API_ENDPOINTS.AUTH.SIGNUP, userData);

        // Don't automatically call me() here - let the component handle it
        // This prevents double calls when the component calls refresh()
        return result;
      } catch (error) {
        // Ensure auth state is cleared on signup failure
        authState.setAuthenticated(false, null, []);
        throw error;
      }
    },

    login: async (credentials) => {
      validateEndpointParams("login", credentials, ["email", "password"]);
      try {
        const result = useMockApi
          ? await mockApi.auth.login(credentials)
          : await api.post(API_ENDPOINTS.AUTH.LOGIN, credentials);

        // Don't automatically call me() here - let the component handle it
        // This prevents double calls when the component calls refresh()
        return result;
      } catch (error) {
        // Ensure auth state is cleared on login failure
        authState.setAuthenticated(false, null, []);
        throw error;
      }
    },

    check: async () => {
      try {
        const result = useMockApi
          ? await mockApi.auth.check()
          : await api.get(API_ENDPOINTS.AUTH.CHECK);

        // Update auth state on successful check
        if (result && result.user) {
          authState.setAuthenticated(true, result.user, result.tenants || []);
        }
        return result;
      } catch (error) {
        // 401 = davvero sloggato (e l'interceptor ha già azzerato lo stato);
        // per errori transitori (rete/5xx) NON tocchiamo la sessione.
        if (error?.status === 401) {
          authState.setAuthenticated(false, null, []);
        }
        throw error;
      }
    },

    me: async () => {
      try {
        const result = useMockApi
          ? await mockApi.auth.me()
          : await api.get(API_ENDPOINTS.AUTH.ME);

        // Update auth state with user and tenant data
        if (result && result.user) {
          authState.setAuthenticated(true, result.user, result.tenants || []);
        }
        return result;
      } catch (error) {
        // Come per check(): solo il 401 azzera lo stato di autenticazione.
        if (error?.status === 401) {
          authState.setAuthenticated(false, null, []);
        }
        throw error;
      }
    },

    refresh: async () => {
      return await authState.refreshSession();
    },

    logout: async () => {
      try {
        const result = await api.post(API_ENDPOINTS.AUTH.LOGOUT);

        // Clear auth state on successful logout
        authState.setAuthenticated(false, null, []);
        return result;
      } catch (error) {
        // Clear auth state even if logout fails
        authState.setAuthenticated(false, null, []);
        throw error;
      }
    },

    // SSO callback for handling SSO authentication
    ssoCallback: async (token, redirectUrl) => {
      validateEndpointParams("ssoCallback", { token }, ["token"]);
      try {
        const result = await api.post(API_ENDPOINTS.SSO.CALLBACK, {
          token,
          redirect_url: redirectUrl,
        });

        // Update auth state after successful SSO
        if (result && result.user) {
          authState.setAuthenticated(true, result.user, result.tenants || []);
        }
        return result;
      } catch (error) {
        authState.setAuthenticated(false, null, []);
        throw error;
      }
    },

    // SSO logout
    ssoLogout: async () => {
      try {
        const result = await api.post(API_ENDPOINTS.SSO.LOGOUT);
        authState.setAuthenticated(false, null, []);
        return result;
      } catch (error) {
        authState.setAuthenticated(false, null, []);
        throw error;
      }
    },

    // SSO redirect helper
    redirectToLogin: (returnUrl = window.location.href) => {
      const encodedReturnUrl = encodeURIComponent(returnUrl);
      window.location.href = `/api/sso/login?returnUrl=${encodedReturnUrl}`;
    },

    // Native login redirect helper
    redirectToNativeLogin: (returnUrl = window.location.pathname) => {
      const encodedReturnUrl = encodeURIComponent(returnUrl);
      window.location.href = `/login?returnUrl=${encodedReturnUrl}`;
    },
  },

  // Bot management services
  bots: {
    list: async () => {
      if (useMockApi) {
        return await mockApi.bots.list();
      } else {
        const response = await api.get(API_ENDPOINTS.BOTS.LIST);
        // The real API returns { bots: [...], total: number, tenant: {...} }
        // We need to extract just the bots array
        return response.bots || [];
      }
    },

    analyzeWebsite: async (websiteUrl) => {
      validateEndpointParams("bots.analyzeWebsite", { websiteUrl }, ["websiteUrl"]);
      if (useMockApi) {
        // Mock response for testing
        return {
          botName: "Sample Bot",
          systemPrompt: "You are a helpful assistant.",
          widgetConfig: {},
          metadata: {},
          suggestedSources: [],
        };
      } else {
        const response = await api.post("/bots/analyze-website", { websiteUrl });
        return response.data;
      }
    },

    // Onboarding v2 — analisi in STREAMING (SSE su POST): onProgress viene
    // chiamata a ogni fase reale del backend ("crawl" | "identity" | "ai"),
    // il return è il risultato completo. Stessa auth dell'axios (cookie +
    // X-Tenant-Id), via fetch perché axios browser non streama.
    analyzeStream: async (path, body, onProgress) => {
      const base = import.meta.env.VITE_API_BASE_URL || "/api";
      const headers = { "Content-Type": "application/json" };
      const activeTenantId = localStorage.getItem("activeTenantId");
      if (activeTenantId && activeTenantId !== "undefined" && activeTenantId !== "null") {
        headers["X-Tenant-Id"] = activeTenantId;
      }
      const response = await fetch(`${base}${path}`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify(body),
      });
      if (!response.ok || !response.body) {
        const err = new Error(`Analysis stream failed (${response.status})`);
        err.response = { status: response.status };
        throw err;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let result = null;
      let streamError = null;

      // SSE: frame separati da doppio newline, payload dopo "data: ".
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split("\n\n");
        buffer = frames.pop();
        for (const frame of frames) {
          const line = frame.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          let evt;
          try {
            evt = JSON.parse(line.slice(6));
          } catch {
            continue;
          }
          if (evt.type === "progress" && onProgress) onProgress(evt.step);
          else if (evt.type === "result") result = evt.data;
          else if (evt.type === "error") {
            streamError = new Error(evt.message || "Analysis failed");
            streamError.data = { message: evt.message };
          }
        }
      }

      if (streamError) throw streamError;
      if (!result) throw new Error("Analysis stream ended without result");
      return result;
    },

    // Onboarding v2: bozza dell'assistente dal solo racconto dell'attività
    // (stessa forma di risposta di analyzeWebsite → riusa loader e review).
    analyzeFromPrompt: async (description) => {
      validateEndpointParams("bots.analyzeFromPrompt", { description }, ["description"]);
      if (useMockApi) {
        return {
          botName: "Sample Assistant",
          systemPrompt: "You are a helpful assistant.",
          widgetConfig: {},
          metadata: {},
          suggestedSources: [],
        };
      }
      const response = await api.post("/bots/analyze-prompt", { description });
      return response.data;
    },

    create: async (data) => {
      validateEndpointParams("bots.create", data, ["name"]);
      if (useMockApi) {
        return await mockApi.bots.create(data);
      } else {
        const response = await api.post(API_ENDPOINTS.BOTS.CREATE, data);
        // The real API returns { bot: {...}, tenant: {...} }
        // We need to extract just the bot object
        return response.bot || response;
      }
    },

    get: async (id) => {
      validateEndpointParams("bots.get", { id }, ["id"]);
      if (useMockApi) {
        return await mockApi.bots.get(id);
      } else {
        const response = await api.get(
          buildUrl(API_ENDPOINTS.BOTS.GET, { id })
        );
        // The real API returns { bot: {...}, tenant: {...} }
        // We need to extract just the bot object
        return response.bot || response;
      }
    },

    update: async (id, data) => {
      validateEndpointParams("bots.update", { id }, ["id"]);
      return useMockApi
        ? await mockApi.bots.update(id, data)
        : await api.put(buildUrl(API_ENDPOINTS.BOTS.UPDATE, { id }), data);
    },

    delete: async (id) => {
      validateEndpointParams("bots.delete", { id }, ["id"]);
      return useMockApi
        ? await mockApi.bots.delete(id)
        : await api.delete(buildUrl(API_ENDPOINTS.BOTS.DELETE, { id }));
    },

    updateWidgetConfig: async (botId, widgetConfig) => {
      console.log('updateWidgetConfig called with:', { botId, widgetConfig });
      validateEndpointParams("bots.updateWidgetConfig", { botId }, ["botId"]);
      if (useMockApi) {
        return await mockApi.bots.updateWidgetConfig(botId, widgetConfig);
      } else {
        const endpoint = API_ENDPOINTS.BOTS.WIDGET_CONFIG(botId);
        console.log('updateWidgetConfig endpoint:', endpoint);
        const response = await api.put(endpoint, { widgetConfig });
        return response;
      }
    },

    getWidgetEmbed: async (botId) => {
      validateEndpointParams("bots.getWidgetEmbed", { botId }, ["botId"]);
      if (useMockApi) {
        return await mockApi.bots.getWidgetEmbed(botId);
      } else {
        const endpoint = API_ENDPOINTS.BOTS.WIDGET_EMBED(botId);
        console.log('Widget embed endpoint:', endpoint);
        const response = await api.get(endpoint);
        return response;
      }
    },
  },

  // Source management services
  sources: {
    // Paginated sources list (parent-only strategy)
    list: async (botId, params = {}) => {
      validateEndpointParams("sources.list", { botId }, ["botId"]);
      if (useMockApi) {
        return await mockApi.sources.list(botId);
      }
      
      // Build query string for pagination and filters
      const searchParams = new URLSearchParams();
      searchParams.set('page', params.page ?? 1);
      searchParams.set('limit', params.limit ?? 20);
      if (params.search) searchParams.set('search', params.search);
      if (params.type) searchParams.set('type', params.type);
      if (params.status) searchParams.set('status', params.status);
      
      const url = buildUrl(API_ENDPOINTS.SOURCES.LIST, { botId });
      return await api.get(`${url}?${searchParams}`);
    },

    // All sources without pagination (for compatibility)
    listAll: async (botId) => {
      validateEndpointParams("sources.listAll", { botId }, ["botId"]);
      return useMockApi
        ? await mockApi.sources.list(botId)
        : await api.get(buildUrl(API_ENDPOINTS.SOURCES.LIST_ALL, { botId }));
    },

    // Sitemap children with pagination
    getSitemapChildren: async (botId, sitemapId, params = {}) => {
      validateEndpointParams("sources.getSitemapChildren", { botId, sitemapId }, ["botId", "sitemapId"]);
      
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set('page', params.page);
      if (params.limit) searchParams.set('limit', params.limit);
      if (params.search) searchParams.set('search', params.search);
      if (params.status) searchParams.set('status', params.status);
      
      const url = buildUrl(API_ENDPOINTS.SOURCES.SITEMAP_CHILDREN, { botId, sitemapId });
      return await api.get(`${url}?${searchParams}`);
    },

    // Sitemap stats
    getSitemapStats: async (botId, sitemapId) => {
      validateEndpointParams("sources.getSitemapStats", { botId, sitemapId }, ["botId", "sitemapId"]);
      const url = buildUrl(API_ENDPOINTS.SOURCES.SITEMAP_STATS, { botId, sitemapId });
      return await api.get(url);
    },

    create: async (botId, data) => {
      validateEndpointParams("sources.create", { botId }, ["botId"]);
      validateEndpointParams("sources.create", data, ["type"]);
      if (useMockApi) {
        return await mockApi.sources.create(botId, data);
      } else {
        const response = await api.post(
          buildUrl(API_ENDPOINTS.SOURCES.CREATE, { botId }),
          data
        );
        // The real API returns { message: "...", source: {...}, queued: true }
        return response.source || response;
      }
    },

    bulkCreate: async (botId, sources) => {
      validateEndpointParams("sources.bulkCreate", { botId }, ["botId"]);
      if (!Array.isArray(sources) || sources.length === 0) {
        throw new Error("sources must be a non-empty array");
      }
      
      const response = await api.post(
        buildUrl(API_ENDPOINTS.SOURCES.BULK_CREATE, { botId }),
        { sources }
      );
      // API returns { message: "...", count: N, sources: [...], queued: true }
      return response;
    },

    get: async (botId, sourceId) => {
      validateEndpointParams("sources.get", { botId, sourceId }, [
        "botId",
        "sourceId",
      ]);
      return useMockApi
        ? await mockApi.sources.get(botId, sourceId)
        : await api.get(
            buildUrl(API_ENDPOINTS.SOURCES.GET, { botId, sourceId })
          );
    },

    delete: async (sourceId) => {
      validateEndpointParams("sources.delete", { sourceId }, ["sourceId"]);
      return await api.delete(
        buildUrl(API_ENDPOINTS.SOURCES.DELETE, { sourceId })
      );
    },

    // Rielabora piu' fonti: { sourceIds } oppure { all: true, filter: { type, status, search } }.
    bulkReingest: async (botId, body) => {
      validateEndpointParams("sources.bulkReingest", { botId }, ["botId"]);
      return await api.post(buildUrl(API_ENDPOINTS.SOURCES.BULK_REINGEST, { botId }), body);
    },
    bulkDelete: async (botId, sourceIds) => {
      validateEndpointParams("sources.bulkDelete", { botId }, ["botId"]);
      if (!Array.isArray(sourceIds) || sourceIds.length === 0) {
        throw new Error("sourceIds must be a non-empty array");
      }
      return await api.post(
        buildUrl(API_ENDPOINTS.SOURCES.BULK_DELETE, { botId }),
        { sourceIds }
      );
    },

    update: async (sourceId, data) => {
      validateEndpointParams("sources.update", { sourceId }, ["sourceId"]);
      const response = await api.patch(
        buildUrl(API_ENDPOINTS.SOURCES.UPDATE, { sourceId }),
        data
      );
      // The real API returns { message, source, queued }
      return response.source || response;
    },

    reingest: async (sourceId) => {
      validateEndpointParams("sources.reingest", { sourceId }, ["sourceId"]);
      return await api.post(
            buildUrl(API_ENDPOINTS.SOURCES.REINGEST, { sourceId })
          );
    },

    // New methods based on real API
    getSchedule: async (sourceId) => {
      validateEndpointParams("sources.getSchedule", { sourceId }, ["sourceId"]);
      return await api.get(
        buildUrl(API_ENDPOINTS.SOURCES.SCHEDULE_GET, { sourceId })
      );
    },

    updateSchedule: async (sourceId, refreshDays) => {
      validateEndpointParams(
        "sources.updateSchedule",
        { sourceId, refreshDays },
        ["sourceId", "refreshDays"]
      );
      return await api.put(
        buildUrl(API_ENDPOINTS.SOURCES.SCHEDULE_UPDATE, { sourceId }),
        {
          refresh_days: refreshDays,
        }
      );
    },

    // Get a short-lived signed URL to open an uploaded document (pdf)
    getViewUrl: async (sourceId) => {
      validateEndpointParams("sources.getViewUrl", { sourceId }, ["sourceId"]);
      return await api.get(buildUrl(API_ENDPOINTS.SOURCES.VIEW, { sourceId }));
    },
  },

  // Product preview services
  products: {
    preview: async (url) => {
      validateEndpointParams("products.preview", { url }, ["url"]);
      return await api.post(API_ENDPOINTS.PRODUCTS.PREVIEW, { url });
    },
  },

  // Upload services
  uploads: {
    generateUrl: async (filename, contentType, botId, kind) => {
      validateEndpointParams(
        "uploads.generateUrl",
        { filename, contentType, botId },
        ["filename", "contentType", "botId"]
      );
      return useMockApi
        ? await mockApi.uploads.getSasUrl(filename, contentType)
        : await api.post(API_ENDPOINTS.UPLOADS.GENERATE_URL, {
            filename,
            contentType,
            botId,
            kind,
          });
    },

    // Legacy method for backward compatibility
    getSasUrl: async (filename, contentType, botId) => {
      return await api.uploads.generateUrl(filename, contentType, botId);
    },

    getStatus: async (blobName) => {
      validateEndpointParams("uploads.getStatus", { blobName }, ["blobName"]);
      return await api.get(
        buildUrl(API_ENDPOINTS.UPLOADS.STATUS, { blobName })
      );
    },

    // Helper for direct file upload to Azure Blob using SAS URL
    uploadFile: async (file, uploadUrl, onProgress = null) => {
      try {
        const config = {
          headers: {
            "Content-Type": file.type,
            // Azure Blob Storage specific headers
            "x-ms-blob-type": "BlockBlob",
          },
          onUploadProgress: onProgress
            ? (progressEvent) => {
                const percentCompleted = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total
                );
                onProgress(percentCompleted);
              }
            : undefined,
          timeout: 300000, // 5 minutes for large files
        };

        // Upload directly to Azure Blob Storage using raw axios (not apiClient)
        // This bypasses our API interceptors and baseURL since we're hitting Azure directly
        const response = await axios.put(uploadUrl, file, config);
        return response;
      } catch (error) {
        console.error("File upload failed:", error);
        throw new Error(i18n.t("dialogs.upload.uploadFailed"));
      }
    },
  },

  // Query services
  query: {
    ask: async (botId, query, options = {}) => {
      validateEndpointParams("query.ask", { botId, query }, ["botId", "query"]);

      if (useMockApi) {
        return await mockApi.query.ask(botId, query);
      } else {
        // Real API uses a single /query endpoint with bot_id in the body
        return await api.post(API_ENDPOINTS.QUERY.ASK, {
          query,
          bot_id: botId,
          options,
        });
      }
    },

    // Legacy method for backward compatibility
    askQuestion: async (botId, question, options = {}) => {
      return await api.query.ask(botId, question, options);
    },
  },

  // Conversation services
  conversations: {
    // params: page, limit, q (ricerca nei messaggi), con_contatto=1 (solo le
    // conversazioni da cui e' arrivato un contatto), status (open = in arrivo,
    // new, read, archived).
    list: async (botId, params = {}) => {
      validateEndpointParams("conversations.list", { botId }, ["botId"]);
      return await api.get(buildUrl(API_ENDPOINTS.CONVERSATIONS.LIST, { botId }), { params });
    },

    exportCsv: async (botId) => {
      validateEndpointParams("conversations.exportCsv", { botId }, ["botId"]);
      return await api.get(buildUrl(API_ENDPOINTS.CONVERSATIONS.EXPORT, { botId }), {
        responseType: "blob",
      });
    },

    get: async (botId, sessionId) => {
      validateEndpointParams("conversations.get", { botId, sessionId }, ["botId", "sessionId"]);
      return await api.get(buildUrl(API_ENDPOINTS.CONVERSATIONS.GET, { botId, sessionId }));
    },

    delete: async (botId, sessionId) => {
      validateEndpointParams("conversations.delete", { botId, sessionId }, ["botId", "sessionId"]);
      return await api.delete(buildUrl(API_ENDPOINTS.CONVERSATIONS.DELETE, { botId, sessionId }));
    },

    // Da leggere (new), letta (read) o archiviata, come i contatti.
    setStatus: async (botId, sessionId, status) => {
      validateEndpointParams("conversations.setStatus", { botId, sessionId }, ["botId", "sessionId"]);
      return await api.patch(buildUrl(API_ENDPOINTS.CONVERSATIONS.UPDATE, { botId, sessionId }), { status });
    },

    getStats: async (botId) => {
      validateEndpointParams("conversations.getStats", { botId }, ["botId"]);
      return await api.get(buildUrl(API_ENDPOINTS.CONVERSATIONS.STATS, { botId }));
    },
  },

  // Contatti raccolti dal modulo in chat
  contacts: {
    list: async (botId, params = {}) => {
      validateEndpointParams("contacts.list", { botId }, ["botId"]);
      return await api.get(buildUrl(API_ENDPOINTS.CONTACTS.LIST, { botId }), { params });
    },

    // Un solo contatto (il link della mail di notifica apre proprio quello).
    // Stessa risorsa di setStatus e delete: il percorso e' lo stesso.
    get: async (botId, contactId) => {
      validateEndpointParams("contacts.get", { botId, contactId }, ["botId", "contactId"]);
      const response = await api.get(buildUrl(API_ENDPOINTS.CONTACTS.UPDATE, { botId, contactId }));
      return response.contact;
    },

    getStats: async (botId) => {
      validateEndpointParams("contacts.getStats", { botId }, ["botId"]);
      return await api.get(buildUrl(API_ENDPOINTS.CONTACTS.STATS, { botId }));
    },

    exportCsv: async (botId) => {
      validateEndpointParams("contacts.exportCsv", { botId }, ["botId"]);
      return await api.get(buildUrl(API_ENDPOINTS.CONTACTS.EXPORT, { botId }), {
        responseType: "blob",
      });
    },

    getForm: async (botId) => {
      validateEndpointParams("contacts.getForm", { botId }, ["botId"]);
      return await api.get(buildUrl(API_ENDPOINTS.CONTACTS.FORM, { botId }));
    },

    saveForm: async (botId, definition) => {
      validateEndpointParams("contacts.saveForm", { botId }, ["botId"]);
      return await api.put(buildUrl(API_ENDPOINTS.CONTACTS.FORM, { botId }), definition);
    },

    // Bozza del modulo da una descrizione: non salva, la bozza va nel compositore.
    draftForm: async (botId, description) => {
      validateEndpointParams("contacts.draftForm", { botId }, ["botId"]);
      return await api.post(buildUrl(API_ENDPOINTS.CONTACTS.FORM_DRAFT, { botId }), { description });
    },

    setStatus: async (botId, contactId, status) => {
      validateEndpointParams("contacts.setStatus", { botId, contactId }, ["botId", "contactId"]);
      return await api.patch(buildUrl(API_ENDPOINTS.CONTACTS.UPDATE, { botId, contactId }), {
        status,
      });
    },

    delete: async (botId, contactId) => {
      validateEndpointParams("contacts.delete", { botId, contactId }, ["botId", "contactId"]);
      return await api.delete(buildUrl(API_ENDPOINTS.CONTACTS.DELETE, { botId, contactId }));
    },

    // Link temporaneo per aprire un file allegato al contatto.
    fileUrl: async (botId, contactId, uploadId) => {
      validateEndpointParams("contacts.fileUrl", { botId, contactId, uploadId }, ["botId", "contactId", "uploadId"]);
      return await api.get(buildUrl(API_ENDPOINTS.CONTACTS.FILE, { botId, contactId, uploadId }));
    },
  },

  // Apps & Integrazioni: interruttori e "quando usarle"
  integrations: {
    get: async (botId) => {
      validateEndpointParams("integrations.get", { botId }, ["botId"]);
      return await api.get(buildUrl(API_ENDPOINTS.INTEGRATIONS, { botId }));
    },

    save: async (botId, changes) => {
      validateEndpointParams("integrations.save", { botId }, ["botId"]);
      return await api.put(buildUrl(API_ENDPOINTS.INTEGRATIONS, { botId }), changes);
    },
  },

  // Analytics services
  analytics: {
    getBot: async (botId, { startDate, endDate } = {}) => {
      validateEndpointParams("analytics.getBot", { botId }, ["botId"]);
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      return await api.get(buildUrl(API_ENDPOINTS.ANALYTICS.BOT, { botId }), { params });
    },

    getBotTimeseries: async (botId, { granularity = "day", startDate, endDate } = {}) => {
      validateEndpointParams("analytics.getBotTimeseries", { botId }, ["botId"]);
      const params = { granularity };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      return await api.get(buildUrl(API_ENDPOINTS.ANALYTICS.BOT_TIMESERIES, { botId }), { params });
    },

    getTenant: async ({ startDate, endDate } = {}) => {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      return await api.get(API_ENDPOINTS.ANALYTICS.TENANT, { params });
    },

    getTopCitations: async (botId, { startDate, endDate, limit = 20 } = {}) => {
      validateEndpointParams("analytics.getTopCitations", { botId }, ["botId"]);
      const params = { limit };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      return await api.get(buildUrl(API_ENDPOINTS.ANALYTICS.TOP_CITATIONS, { botId }), { params });
    },

    getTopCards: async (botId, { startDate, endDate, limit = 20 } = {}) => {
      validateEndpointParams("analytics.getTopCards", { botId }, ["botId"]);
      const params = { limit };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      return await api.get(buildUrl(API_ENDPOINTS.ANALYTICS.TOP_CARDS, { botId }), { params });
    },
  },

  // Billing services
  billing: {
    getSubscription: async () => {
      return await api.get(API_ENDPOINTS.BILLING.SUBSCRIPTION);
    },
    listPlans: async () => {
      return await api.get(API_ENDPOINTS.BILLING.CATALOG);
    },
    updateDomains: async (domains) => {
      return await api.put(API_ENDPOINTS.BILLING.DOMAINS, { domains });
    },
    updateSubscription: async ({ plan_key, mode, stripe_price_id }) => {
      return await api.post(API_ENDPOINTS.BILLING.SUBSCRIPTION, { plan_key, mode, stripe_price_id });
    },
    getProfile: async () => {
      return await api.get(API_ENDPOINTS.BILLING.PROFILE);
    },
    updateProfile: async (billing) => {
      return await api.put(API_ENDPOINTS.BILLING.PROFILE, { billing });
    },
    // Abbonamento a pagamento (Stripe): stato dell'account, pagamento,
    // conferma al ritorno, portale clienti.
    getAccount: async () => {
      return await api.get(API_ENDPOINTS.BILLING.ACCOUNT);
    },
    // interval: "yearly" (di norma) o "monthly", se il piano ha il prezzo mensile.
    checkout: async (plan_key, interval = "yearly") => {
      return await api.post(API_ENDPOINTS.BILLING.CHECKOUT, { plan_key, interval });
    },
    confirmCheckout: async (session_id) => {
      return await api.post(API_ENDPOINTS.BILLING.CHECKOUT_CONFIRM, { session_id });
    },
    // Senza argomenti il portale generico; plan_key: la conferma del passaggio
    // a quel piano; disdici: la disdetta.
    portal: async ({ plan_key, interval, disdici } = {}) => {
      return await api.post(API_ENDPOINTS.BILLING.PORTAL, { plan_key, interval, disdici });
    },
    // Rilegge subito l'abbonamento da Stripe (al ritorno dal portale).
    sync: async () => {
      return await api.post(API_ENDPOINTS.BILLING.SYNC);
    },
    // Annulla il passaggio a un piano piu' economico programmato per il rinnovo.
    cancelScheduledChange: async () => {
      return await api.post(API_ENDPOINTS.BILLING.SCHEDULED_CANCEL);
    },
  },
};

export default apiClient;
